/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

import apiClient from "../../../../services/api-client";
import { RootState } from "../../../../store/rootReducer";
import { productToSyncPayload } from "../../utils/productToSyncItem";
import { SyncCartReducerAction } from "../cart.slice";
import {
  CartAPIError,
  CartObject,
  Discount,
  SyncCartPayload,
  SyncCartResponse,
} from "../cart.types";

let controller = new AbortController();

const REMOVE: Discount[] = [
  { offerId: "RT0001", offerName: "Shareholder discount", offerDescription: "" },
];

const removeOfferId = REMOVE.map((x) => x.offerId);

const transformResponse = (data: SyncCartResponse) => {
  data.items.forEach((item) => {
    item.discounts = item.discounts?.filter((x) => !removeOfferId.includes(x.offerId));
    if (item.discounts?.length === 0) {
      item.discounts = undefined;
    }
  });

  return data;
};

export const syncCart = createAsyncThunk<
  SyncCartReducerAction,
  { cart: CartObject; branchId: string; accountNumber: string },
  { rejectValue: CartAPIError; getState: RootState }
>("cart/sync", async ({ cart, branchId }, { rejectWithValue }) => {
  try {
    // Cancel the existing request if there is one
    controller.abort();

    controller = new AbortController();
    const signal = controller.signal;

    const payload: SyncCartPayload = {
      accountNumber: cart.accountNumber,
      branchId,
      cartId: cart.onlineCartId,
      items: cart.items.map((item) => productToSyncPayload(item)),
    };

    const request = await apiClient.post<SyncCartResponse, SyncCartPayload>(
      `/carts/sync`,
      payload,
      {
        signal: signal,
      }
    );
    if ((request as unknown as AxiosError<CartAPIError>)?.code) {
      return rejectWithValue(
        (request as unknown as AxiosError<CartAPIError>)?.response?.data as CartAPIError
      );
    }
    return {
      data: transformResponse(request.data),
      offlineCartId: cart.offlineCartId,
    } as SyncCartReducerAction;
  } catch (err) {
    const error: AxiosError = err as AxiosError;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data as CartAPIError);
  }
});
